import { MutationOptions, useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { duplicateWorkflow } from 'api/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { queryClient } from 'queries/queryClient';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { Workflow } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface UseDuplicateWorkflowParams {
    workflowId: number;
}

const useDuplicateWorkflow = (
    options?: MutationOptions<Workflow, GivebutterAxiosError, UseDuplicateWorkflowParams>,
) => {
    const { onSuccess, onError, ...restOptions } = options || {};
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();

    return useMutation<Workflow, GivebutterAxiosError, UseDuplicateWorkflowParams>({
        mutationFn: ({ workflowId }) => duplicateWorkflow(accountId, workflowId),
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: workflowQueryKeys.workflows(accountId),
            });
            onSuccess?.(data, variables, context);
            success('Your workflow has been duplicated!');
        },
        onError: (e, variables, context) => {
            error(e.response?.data.message || 'Failed to duplicate workflow. Please try again.');
            onError?.(e, variables, context);
        },
        ...restOptions,
    });
};

export { useDuplicateWorkflow };
